// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { CycleTimeMetric } from '@/report/api/types';
import {
  CYCLE_TIME_SMALL_SAMPLE_THRESHOLD,
  formatCycleTimeDays,
} from '@/report/components/cycle-time-metrics';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import type { ReactElement, ReactNode } from 'react';

const DISTRIBUTION_HEIGHT_PX = 26;
const INLINE_AXIS_LABEL_HEIGHT_PX = 18;
const DISTRIBUTION_SCALE_HEIGHT_PX =
  DISTRIBUTION_HEIGHT_PX + INLINE_AXIS_LABEL_HEIGHT_PX;
const TRACK_HEIGHT_PX = 4;
const BOX_HEIGHT_PX = 10;
const MARKER_SIZE_PX = 7;
const MEDIAN_WIDTH_PX = 2;
const TOOLTIP_Z_INDEX = 2100;
const TOOLTIP_WIDTH_PX = 380;
const TOOLTIP_SCALE_HEIGHT_PX = 128;
const TOOLTIP_LABEL_WIDTH_PX = 76;
const TOOLTIP_LABEL_WIDTH_PCT =
  (TOOLTIP_LABEL_WIDTH_PX / TOOLTIP_WIDTH_PX) * 100;
const TOOLTIP_LABEL_GAP_PCT = 1.5;
const TOOLTIP_LABEL_LANE_HEIGHT_PX = 30;
const TOOLTIP_TOP_LABEL_TOP_PX = 0;
const TOOLTIP_LABEL_TO_TRACK_GAP_PX = 32;
const TOOLTIP_TRACK_TO_BOTTOM_LABEL_GAP_PX = 32;
const TOOLTIP_TOP_GUIDE_LABEL_OFFSET_PX = 34;
const TOOLTIP_BOTTOM_GUIDE_TOP_OFFSET_PX = 10;
const TOOLTIP_BOTTOM_GUIDE_BOTTOM_GAP_PX = 8;
const TOOLTIP_SCALE_BOTTOM_PADDING_PX = 4;
const TOOLTIP_GUIDE_WIDTH_PX = '1px';
const TOOLTIP_TICK_HEIGHT_PX = 14;
const TOOLTIP_BOX_HEIGHT_PX = 8;
const TOOLTIP_MARKER_SIZE_PX = 6;
const TOOLTIP_MEDIAN_HEIGHT_PX = 20;
const TOOLTIP_COLLAPSED_TRACK_TOP_PX = 62;
const TOOLTIP_COLLAPSED_BOTTOM_LABEL_TOP_PX = 94;
const TOOLTIP_COLLAPSED_MARKER_HEIGHT_PX = 30;

interface DistributionValues {
  p10Days: number;
  p25Days: number;
  medianDays: number;
  avgDays: number;
  p75Days: number;
  p90Days: number;
}

interface DistributionPercents {
  p25Pct: number;
  p75Pct: number;
  medianPct: number;
  avgPct: number;
}

interface DistributionTooltipLabel {
  key: string;
  label: string;
  value: number;
  leftPct: number;
  row: 'top' | 'bottom';
  lane: number;
}

interface CycleTimeDistributionBarProps {
  metric: CycleTimeMetric;
  label: string;
  tooltip: string;
  isLoading?: boolean;
  singularNoun: string;
}

function toPercent(value: number, min: number, max: number): number {
  if (max === min) {
    return 50;
  }

  const raw = ((value - min) / (max - min)) * 100;
  return Math.min(100, Math.max(0, raw));
}

function getDistributionValues(
  metric: CycleTimeMetric
): DistributionValues | null {
  if (
    metric.p10Days === null ||
    metric.p25Days === null ||
    metric.medianDays === null ||
    metric.avgDays === null ||
    metric.p75Days === null ||
    metric.p90Days === null
  ) {
    return null;
  }

  return {
    p10Days: metric.p10Days,
    p25Days: metric.p25Days,
    medianDays: metric.medianDays,
    avgDays: metric.avgDays,
    p75Days: metric.p75Days,
    p90Days: metric.p90Days,
  };
}

function getDistributionPercents(
  values: DistributionValues
): DistributionPercents {
  return {
    p25Pct: toPercent(values.p25Days, values.p10Days, values.p90Days),
    p75Pct: toPercent(values.p75Days, values.p10Days, values.p90Days),
    medianPct: toPercent(values.medianDays, values.p10Days, values.p90Days),
    avgPct: toPercent(values.avgDays, values.p10Days, values.p90Days),
  };
}

function isCollapsedDistribution(values: DistributionValues): boolean {
  return values.p10Days === values.p90Days;
}

function getTooltipLabelTransform(leftPct: number): string {
  if (leftPct <= 8) {
    return 'translateX(0)';
  }
  if (leftPct >= 92) {
    return 'translateX(-100%)';
  }
  return 'translateX(-50%)';
}

function getTooltipLabelTextAlign(
  leftPct: number
): 'left' | 'center' | 'right' {
  if (leftPct <= 8) {
    return 'left';
  }
  if (leftPct >= 92) {
    return 'right';
  }
  return 'center';
}

function getTooltipLabelRange(label: DistributionTooltipLabel) {
  if (label.leftPct <= 8) {
    return {
      leftPct: label.leftPct,
      rightPct: label.leftPct + TOOLTIP_LABEL_WIDTH_PCT,
    };
  }
  if (label.leftPct >= 92) {
    return {
      leftPct: label.leftPct - TOOLTIP_LABEL_WIDTH_PCT,
      rightPct: label.leftPct,
    };
  }

  return {
    leftPct: label.leftPct - TOOLTIP_LABEL_WIDTH_PCT / 2,
    rightPct: label.leftPct + TOOLTIP_LABEL_WIDTH_PCT / 2,
  };
}

function tooltipLabelRangesOverlap(
  leftLabel: DistributionTooltipLabel,
  rightLabel: DistributionTooltipLabel
): boolean {
  const leftRange = getTooltipLabelRange(leftLabel);
  const rightRange = getTooltipLabelRange(rightLabel);
  return (
    leftRange.rightPct + TOOLTIP_LABEL_GAP_PCT > rightRange.leftPct &&
    rightRange.rightPct + TOOLTIP_LABEL_GAP_PCT > leftRange.leftPct
  );
}

function groupTooltipLabels(
  labels: DistributionTooltipLabel[]
): DistributionTooltipLabel[] {
  const labelGroups = new Map<string, DistributionTooltipLabel[]>();

  for (const label of labels) {
    const key = `${label.row}-${label.leftPct.toFixed(2)}-${label.value.toFixed(2)}`;
    const existingLabels = labelGroups.get(key) ?? [];
    existingLabels.push(label);
    labelGroups.set(key, existingLabels);
  }

  return Array.from(labelGroups.entries()).flatMap(([key, groupLabels]) => {
    const firstLabel = groupLabels[0];
    if (firstLabel === undefined) {
      return [];
    }

    return [
      {
        key,
        label: groupLabels.map((label) => label.label).join(' / '),
        value: firstLabel.value,
        leftPct: firstLabel.leftPct,
        row: firstLabel.row,
        lane: 0,
      },
    ];
  });
}

function assignTooltipLabelLanes(
  labels: DistributionTooltipLabel[]
): DistributionTooltipLabel[] {
  const labelsByRow = new Map<
    DistributionTooltipLabel['row'],
    DistributionTooltipLabel[]
  >();

  for (const label of labels) {
    labelsByRow.set(label.row, [...(labelsByRow.get(label.row) ?? []), label]);
  }

  return Array.from(labelsByRow.values()).flatMap((rowLabels) => {
    const lanes: DistributionTooltipLabel[][] = [];
    return [...rowLabels]
      .sort((leftLabel, rightLabel) => leftLabel.leftPct - rightLabel.leftPct)
      .map((label) => {
        const candidateLabel = { ...label, lane: 0 };
        const laneIndex = lanes.findIndex((laneLabels) =>
          laneLabels.every(
            (laneLabel) => !tooltipLabelRangesOverlap(candidateLabel, laneLabel)
          )
        );
        const resolvedLaneIndex = laneIndex === -1 ? lanes.length : laneIndex;
        const resolvedLabel = { ...label, lane: resolvedLaneIndex };
        if (lanes[resolvedLaneIndex] === undefined) {
          lanes[resolvedLaneIndex] = [];
        }
        lanes[resolvedLaneIndex].push(resolvedLabel);
        return resolvedLabel;
      });
  });
}

function getTooltipLaneCount(
  labels: DistributionTooltipLabel[],
  row: 'top' | 'bottom'
): number {
  const rowLanes = labels
    .filter((label) => label.row === row)
    .map((label) => label.lane);
  if (rowLanes.length === 0) {
    return 1;
  }

  return Math.max(...rowLanes) + 1;
}

function DistributionTooltipWrapper(props: {
  title: ReactNode;
  children: ReactElement;
}) {
  return (
    <Tooltip
      arrow
      placement="bottom"
      slotProps={{
        popper: {
          sx: {
            zIndex: TOOLTIP_Z_INDEX,
          },
        },
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 8,
            width: TOOLTIP_WIDTH_PX,
            maxWidth: TOOLTIP_WIDTH_PX,
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden',
          },
        },
        arrow: {
          sx: { color: 'background.paper' },
        },
      }}
      title={props.title}
    >
      {props.children}
    </Tooltip>
  );
}

function CollapsedDistributionTooltipScale(props: { value: number }) {
  return (
    <Box
      data-testid="cycle-time-distribution-tooltip-collapsed-scale"
      sx={{
        position: 'relative',
        height: TOOLTIP_SCALE_HEIGHT_PX,
        width: '100%',
        mt: 0.5,
        overflow: 'hidden',
        contain: 'layout paint',
      }}
    >
      <Typography
        variant="caption"
        component="span"
        sx={{
          position: 'absolute',
          top: TOOLTIP_TOP_LABEL_TOP_PX,
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'text.secondary',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        All percentiles
      </Typography>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: TOOLTIP_COLLAPSED_TRACK_TOP_PX,
          height: TRACK_HEIGHT_PX,
          borderRadius: TRACK_HEIGHT_PX / 2,
          bgcolor: 'divider',
        }}
      />
      <Box
        data-testid="cycle-time-distribution-tooltip-collapsed-marker"
        sx={{
          position: 'absolute',
          left: '50%',
          top:
            TOOLTIP_COLLAPSED_TRACK_TOP_PX -
            (TOOLTIP_COLLAPSED_MARKER_HEIGHT_PX - TRACK_HEIGHT_PX) / 2,
          width: MEDIAN_WIDTH_PX,
          height: TOOLTIP_COLLAPSED_MARKER_HEIGHT_PX,
          borderRadius: MEDIAN_WIDTH_PX / 2,
          bgcolor: 'text.primary',
          transform: 'translateX(-50%)',
        }}
      />
      <Typography
        variant="caption"
        component="span"
        sx={{
          position: 'absolute',
          top: TOOLTIP_COLLAPSED_BOTTOM_LABEL_TOP_PX,
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'text.primary',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {formatCycleTimeDays(props.value)}
      </Typography>
    </Box>
  );
}

function DistributionTooltipScale(props: { values: DistributionValues }) {
  if (isCollapsedDistribution(props.values)) {
    return <CollapsedDistributionTooltipScale value={props.values.p10Days} />;
  }

  const percents = getDistributionPercents(props.values);
  const labels: DistributionTooltipLabel[] = [
    {
      key: 'p10',
      label: '10th',
      value: props.values.p10Days,
      leftPct: 0,
      row: 'top',
      lane: 0,
    },
    {
      key: 'p25',
      label: '25th',
      value: props.values.p25Days,
      leftPct: percents.p25Pct,
      row: 'bottom',
      lane: 0,
    },
    {
      key: 'median',
      label: 'Median',
      value: props.values.medianDays,
      leftPct: percents.medianPct,
      row: 'top',
      lane: 0,
    },
    {
      key: 'average',
      label: 'Average',
      value: props.values.avgDays,
      leftPct: percents.avgPct,
      row: 'bottom',
      lane: 0,
    },
    {
      key: 'p75',
      label: '75th',
      value: props.values.p75Days,
      leftPct: percents.p75Pct,
      row: 'top',
      lane: 0,
    },
    {
      key: 'p90',
      label: '90th',
      value: props.values.p90Days,
      leftPct: 100,
      row: 'bottom',
      lane: 0,
    },
  ];
  const labelGroups = assignTooltipLabelLanes(groupTooltipLabels(labels));
  const topLaneCount = getTooltipLaneCount(labelGroups, 'top');
  const bottomLaneCount = getTooltipLaneCount(labelGroups, 'bottom');
  const trackTopPx =
    TOOLTIP_TOP_LABEL_TOP_PX +
    topLaneCount * TOOLTIP_LABEL_LANE_HEIGHT_PX +
    TOOLTIP_LABEL_TO_TRACK_GAP_PX;
  const bottomLabelTopPx = trackTopPx + TOOLTIP_TRACK_TO_BOTTOM_LABEL_GAP_PX;
  const scaleHeightPx =
    bottomLabelTopPx +
    bottomLaneCount * TOOLTIP_LABEL_LANE_HEIGHT_PX +
    TOOLTIP_SCALE_BOTTOM_PADDING_PX;

  return (
    <Box
      data-testid="cycle-time-distribution-tooltip-scale"
      data-top-lanes={topLaneCount}
      data-bottom-lanes={bottomLaneCount}
      sx={{
        position: 'relative',
        height: scaleHeightPx,
        width: '100%',
        mt: 0.5,
        overflow: 'hidden',
        contain: 'layout paint',
      }}
    >
      {labelGroups.map((label) => (
        <Box
          key={`${label.key}-guide`}
          data-testid="cycle-time-distribution-tooltip-guide"
          data-row={label.row}
          style={{ left: `${label.leftPct}%` }}
          sx={{
            position: 'absolute',
            top:
              label.row === 'top'
                ? TOOLTIP_TOP_LABEL_TOP_PX +
                  label.lane * TOOLTIP_LABEL_LANE_HEIGHT_PX +
                  TOOLTIP_TOP_GUIDE_LABEL_OFFSET_PX
                : trackTopPx + TOOLTIP_BOTTOM_GUIDE_TOP_OFFSET_PX,
            height:
              label.row === 'top'
                ? Math.max(
                    0,
                    trackTopPx -
                      TOOLTIP_TICK_HEIGHT_PX / 2 -
                      (TOOLTIP_TOP_LABEL_TOP_PX +
                        label.lane * TOOLTIP_LABEL_LANE_HEIGHT_PX +
                        TOOLTIP_TOP_GUIDE_LABEL_OFFSET_PX)
                  )
                : Math.max(
                    0,
                    bottomLabelTopPx +
                      label.lane * TOOLTIP_LABEL_LANE_HEIGHT_PX -
                      trackTopPx -
                      TOOLTIP_BOTTOM_GUIDE_TOP_OFFSET_PX -
                      TOOLTIP_BOTTOM_GUIDE_BOTTOM_GAP_PX
                  ),
            width: TOOLTIP_GUIDE_WIDTH_PX,
            bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.26),
            transform: 'translateX(-50%)',
          }}
        />
      ))}
      <Box
        data-testid="cycle-time-distribution-tooltip-track"
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: trackTopPx,
          height: TRACK_HEIGHT_PX,
          borderRadius: TRACK_HEIGHT_PX / 2,
          bgcolor: 'divider',
        }}
      />
      <Box
        data-testid="cycle-time-distribution-tooltip-box"
        style={{
          left: `${percents.p25Pct}%`,
          right: `${100 - percents.p75Pct}%`,
        }}
        sx={{
          position: 'absolute',
          top: trackTopPx - (TOOLTIP_BOX_HEIGHT_PX - TRACK_HEIGHT_PX) / 2,
          height: TOOLTIP_BOX_HEIGHT_PX,
          borderRadius: 0.5,
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.14),
        }}
      />
      <Box
        data-testid="cycle-time-distribution-tooltip-median"
        data-left-pct={percents.medianPct}
        style={{ left: `${percents.medianPct}%` }}
        sx={{
          position: 'absolute',
          top: trackTopPx - (TOOLTIP_MEDIAN_HEIGHT_PX - TRACK_HEIGHT_PX) / 2,
          width: MEDIAN_WIDTH_PX,
          height: TOOLTIP_MEDIAN_HEIGHT_PX,
          borderRadius: MEDIAN_WIDTH_PX / 2,
          bgcolor: 'text.primary',
          transform: 'translateX(-50%)',
        }}
      />
      <Box
        data-testid="cycle-time-distribution-tooltip-average"
        data-left-pct={percents.avgPct}
        style={{ left: `${percents.avgPct}%` }}
        sx={{
          position: 'absolute',
          top: trackTopPx + TRACK_HEIGHT_PX / 2,
          width: TOOLTIP_MARKER_SIZE_PX,
          height: TOOLTIP_MARKER_SIZE_PX,
          borderRadius: '50%',
          border: 1,
          borderColor: 'text.primary',
          bgcolor: 'background.paper',
          transform: 'translate(-50%, -50%)',
        }}
      />
      {[
        { key: 'p10', leftPct: 0 },
        { key: 'p25', leftPct: percents.p25Pct },
        { key: 'p75', leftPct: percents.p75Pct },
        { key: 'p90', leftPct: 100 },
      ].map((tick) => (
        <Box
          key={tick.key}
          data-testid="cycle-time-distribution-tooltip-tick"
          style={{ left: `${tick.leftPct}%` }}
          sx={{
            position: 'absolute',
            top: trackTopPx - (TOOLTIP_TICK_HEIGHT_PX - TRACK_HEIGHT_PX) / 2,
            width: TOOLTIP_GUIDE_WIDTH_PX,
            height: TOOLTIP_TICK_HEIGHT_PX,
            bgcolor: 'text.secondary',
            transform: 'translateX(-50%)',
          }}
        />
      ))}
      {labelGroups.map((label) => (
        <Typography
          key={label.key}
          data-testid="cycle-time-distribution-tooltip-label"
          data-label={label.label}
          data-lane={label.lane}
          variant="caption"
          component="span"
          style={{ left: `${label.leftPct}%` }}
          sx={{
            position: 'absolute',
            top:
              label.row === 'top'
                ? TOOLTIP_TOP_LABEL_TOP_PX +
                  label.lane * TOOLTIP_LABEL_LANE_HEIGHT_PX
                : bottomLabelTopPx + label.lane * TOOLTIP_LABEL_LANE_HEIGHT_PX,
            width: TOOLTIP_LABEL_WIDTH_PX,
            transform: getTooltipLabelTransform(label.leftPct),
            textAlign: getTooltipLabelTextAlign(label.leftPct),
            lineHeight: 1.15,
            color: 'text.secondary',
            whiteSpace: 'nowrap',
          }}
        >
          <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
            {label.label}
          </Box>
          <Box
            component="span"
            sx={{
              display: 'block',
              color: 'text.primary',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatCycleTimeDays(label.value)}
          </Box>
        </Typography>
      ))}
    </Box>
  );
}

function DistributionTooltipContent(props: {
  metric: CycleTimeMetric;
  label: string;
  tooltip: string;
  showDistribution?: boolean;
  singularNoun: string;
}) {
  const values = getDistributionValues(props.metric);
  const shouldShowDistribution = props.showDistribution !== false;

  return (
    <Stack spacing={1.25} sx={{ p: 0.5, width: '100%' }}>
      <Stack spacing={0.25}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {props.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {props.tooltip}
        </Typography>
      </Stack>
      {shouldShowDistribution && values !== null ? (
        <DistributionTooltipScale values={values} />
      ) : (
        <Typography variant="caption" color="text.secondary">
          Too few{' '}
          {pluralize(2, { singular: props.singularNoun, includeCount: false })}{' '}
          for distribution
        </Typography>
      )}
    </Stack>
  );
}

function DistributionAxisLabels(props: {
  leftValue: number;
  rightValue: number;
}) {
  return (
    <Box
      data-testid="cycle-time-distribution-axis"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: INLINE_AXIS_LABEL_HEIGHT_PX,
      }}
    >
      <Typography
        data-testid="cycle-time-distribution-axis-min"
        variant="caption"
        color="text.secondary"
        sx={{
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatCycleTimeDays(props.leftValue)}
      </Typography>
      <Typography
        data-testid="cycle-time-distribution-axis-max"
        variant="caption"
        color="text.secondary"
        sx={{
          lineHeight: 1,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatCycleTimeDays(props.rightValue)}
      </Typography>
    </Box>
  );
}

function SmallSampleIndicator(props: {
  metric: CycleTimeMetric;
  label: string;
  tooltip: string;
  singularNoun: string;
}) {
  return (
    <DistributionTooltipWrapper
      title={
        <DistributionTooltipContent
          metric={props.metric}
          label={props.label}
          tooltip={props.tooltip}
          showDistribution={false}
          singularNoun={props.singularNoun}
        />
      }
    >
      <Stack spacing={0.5} sx={{ mt: 1.5, cursor: 'help' }}>
        <Box
          data-testid="cycle-time-distribution-small-sample"
          sx={{
            position: 'relative',
            height: DISTRIBUTION_HEIGHT_PX,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              height: TRACK_HEIGHT_PX,
              borderRadius: TRACK_HEIGHT_PX / 2,
              bgcolor: 'divider',
              transform: 'translateY(-50%)',
            }}
          />
          {props.metric.medianDays !== null && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: MARKER_SIZE_PX,
                height: MARKER_SIZE_PX,
                borderRadius: '50%',
                bgcolor: 'text.secondary',
                transform: 'translate(-50%, -50%)',
              }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Too few{' '}
          {pluralize(2, { singular: props.singularNoun, includeCount: false })}{' '}
          for distribution
        </Typography>
      </Stack>
    </DistributionTooltipWrapper>
  );
}

export function CycleTimeDistributionBar(props: CycleTimeDistributionBarProps) {
  if (props.isLoading) {
    return (
      <Skeleton
        data-testid="cycle-time-distribution-skeleton"
        variant="rectangular"
        height={DISTRIBUTION_SCALE_HEIGHT_PX}
        sx={{ borderRadius: 1, mt: 1.5, mb: 2 }}
      />
    );
  }

  if (props.metric.count === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
        No {pluralize(2, { singular: props.singularNoun, includeCount: false })}
      </Typography>
    );
  }

  if (props.metric.count < CYCLE_TIME_SMALL_SAMPLE_THRESHOLD) {
    return (
      <SmallSampleIndicator
        metric={props.metric}
        label={props.label}
        tooltip={props.tooltip}
        singularNoun={props.singularNoun}
      />
    );
  }

  const values = getDistributionValues(props.metric);

  if (values === null) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
        Distribution unavailable
      </Typography>
    );
  }

  if (isCollapsedDistribution(values)) {
    return (
      <DistributionTooltipWrapper
        title={
          <DistributionTooltipContent
            metric={props.metric}
            label={props.label}
            tooltip={props.tooltip}
            singularNoun={props.singularNoun}
          />
        }
      >
        <Stack
          data-testid="cycle-time-distribution-bar"
          spacing={0.25}
          sx={{
            mt: 1.5,
            cursor: 'help',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              height: DISTRIBUTION_HEIGHT_PX,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                height: TRACK_HEIGHT_PX,
                borderRadius: TRACK_HEIGHT_PX / 2,
                bgcolor: 'divider',
                transform: 'translateY(-50%)',
              }}
            />
            <Box
              data-testid="cycle-time-distribution-collapsed-marker"
              data-left-pct={50}
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: MEDIAN_WIDTH_PX,
                height: BOX_HEIGHT_PX + 6,
                borderRadius: MEDIAN_WIDTH_PX / 2,
                bgcolor: 'text.primary',
                transform: 'translate(-50%, -50%)',
              }}
            />
          </Box>
          <DistributionAxisLabels
            leftValue={values.p10Days}
            rightValue={values.p90Days}
          />
        </Stack>
      </DistributionTooltipWrapper>
    );
  }

  const { p25Pct, p75Pct, medianPct, avgPct } = getDistributionPercents(values);

  return (
    <DistributionTooltipWrapper
      title={
        <DistributionTooltipContent
          metric={props.metric}
          label={props.label}
          tooltip={props.tooltip}
          singularNoun={props.singularNoun}
        />
      }
    >
      <Stack
        data-testid="cycle-time-distribution-bar"
        spacing={0.25}
        sx={{
          mt: 1.5,
          cursor: 'help',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            height: DISTRIBUTION_HEIGHT_PX,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              height: TRACK_HEIGHT_PX,
              borderRadius: TRACK_HEIGHT_PX / 2,
              bgcolor: 'divider',
              transform: 'translateY(-50%)',
            }}
          />
          <Box
            data-testid="cycle-time-distribution-box"
            data-left-pct={p25Pct}
            data-right-pct={p75Pct}
            style={{ left: `${p25Pct}%`, right: `${100 - p75Pct}%` }}
            sx={{
              position: 'absolute',
              top: '50%',
              height: BOX_HEIGHT_PX,
              borderRadius: 0.5,
              bgcolor: (theme) => alpha(theme.palette.text.primary, 0.18),
              transform: 'translateY(-50%)',
            }}
          />
          <Box
            data-testid="cycle-time-distribution-median"
            data-left-pct={medianPct}
            style={{ left: `${medianPct}%` }}
            sx={{
              position: 'absolute',
              top: '50%',
              width: MEDIAN_WIDTH_PX,
              height: BOX_HEIGHT_PX + 6,
              borderRadius: MEDIAN_WIDTH_PX / 2,
              bgcolor: 'text.primary',
              transform: 'translate(-50%, -50%)',
            }}
          />
          <Box
            data-testid="cycle-time-distribution-average"
            data-left-pct={avgPct}
            style={{ left: `${avgPct}%` }}
            sx={{
              position: 'absolute',
              top: '50%',
              width: MARKER_SIZE_PX,
              height: MARKER_SIZE_PX,
              borderRadius: '50%',
              border: 1,
              borderColor: 'text.primary',
              bgcolor: 'background.paper',
              transform: 'translate(-50%, -50%)',
            }}
          />
        </Box>
        <DistributionAxisLabels
          leftValue={values.p10Days}
          rightValue={values.p90Days}
        />
      </Stack>
    </DistributionTooltipWrapper>
  );
}
